import type { FolderAclDtoOutput } from '@batiflow/api-client';
import { FormField, SkeletonList, isolate } from '@batiflow/ui';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  Select,
  Switch,
  __css,
  tokens,
} from '@fluentui/react-components';
import { Delete20Regular, People20Regular, PersonAdd20Regular } from '@fluentui/react-icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotify } from '@/shared/hooks/use-notify';
import {
  useFolderAcl,
  useOrganisationMembers,
  useRefreshFolders,
  useReplaceFolderAcl,
} from './api';
import {
  aclBody,
  draftOf,
  folderErrorMessage,
  isStaleVersion,
  isStepUpRequired,
  sameAcl,
  type AclDraft,
  type AclDraftEntry,
  type AclRelation,
  type Folder,
} from './model';

const useStyles = __css({"body":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1noh358"},"hint":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","sj55zd":"fkfq4zb"},"list":{"dclx09":"ftrb29c","Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","B1hrpyx":"f1jat206","mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1wn3y0l"},"entry":{"mc9l5x":"f22iagw","Bt984gj":"f122n59","Eh141a":"fni485r","i8kkvl":"fsnqrgy","Belr9w4":"f778t4r","Dbcxam":0,"rjzwhg":0,"Bblux5w":"f47sw1k","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"f5j02bv","Beyfa6y":0,"Bbmb7ep":0,"Btl43ni":0,"B7oj6ja":0,"Dimara":"ft85np5","De3pzq":"f1dmdbja"},"entryName":{"mc9l5x":"f22iagw","Bt984gj":"f122n59","i8kkvl":"f1ufnopg","Bh6795r":"fqerorx","xawz":"fkpcsv1","Bgto7am":"fuzgk9b","Btd35i7":"fjksvth"},"relation":{"Bgto7am":"fqhbt98"},"adder":{"mc9l5x":"f22iagw","Eh141a":"fni485r","Bt984gj":"fgs5rwf","i8kkvl":"fsnqrgy","Belr9w4":"f1wn3y0l"},"adderField":{"Bh6795r":"fqerorx","xawz":"ffq3mkc"}});

const RELATIONS: readonly AclRelation[] = ['reader', 'writer'];
const isRelation = (value: string): value is AclRelation =>
  (RELATIONS as readonly string[]).includes(value);

interface FormProps {
  orgId: string;
  spaceId: string;
  folder: Folder;
  acl: FolderAclDtoOutput;
  onClose: () => void;
}

function PermissionsForm({ orgId, spaceId, folder, acl, onClose }: FormProps) {
  const { t } = useTranslation('documents');
  const styles = useStyles();
  const notify = useNotify();
  const members = useOrganisationMembers(orgId);
  const replace = useReplaceFolderAcl(spaceId);
  const refresh = useRefreshFolders(spaceId);

  const initial = draftOf(acl);
  const [draft, setDraft] = useState<AclDraft>(initial);
  const [pick, setPick] = useState('');
  const [failure, setFailure] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const custom = draft.mode === 'custom';
  const dirty = !sameAcl(draft, initial);
  const everyoneListed = draft.entries.some(
    (entry) => entry.subjectType === 'organization_members',
  );
  const available = (members.data?.items ?? []).filter(
    (member) =>
      !draft.entries.some(
        (entry) => entry.subjectType === 'user' && entry.subjectId === member.userId,
      ),
  );

  const change = (next: Partial<AclDraft>) => {
    setDraft((current) => ({ ...current, ...next }));
    setFailure(null);
  };
  const addEntry = (entry: AclDraftEntry) => {
    change({ entries: [...draft.entries, entry] });
  };
  const addPerson = () => {
    const member = available.find((candidate) => candidate.userId === pick);
    if (!member) return;
    addEntry({
      relation: 'reader',
      subjectType: 'user',
      subjectId: member.userId,
      displayName: member.displayName,
    });
    setPick('');
  };
  const addEveryone = () => {
    addEntry({
      relation: 'reader',
      subjectType: 'organization_members',
      subjectId: orgId,
      displayName: t('permissions.everyone'),
    });
  };
  const nameOf = (entry: AclDraftEntry) =>
    entry.subjectType === 'organization_members' ? t('permissions.everyone') : entry.displayName;

  const save = async () => {
    if (pending || !dirty) return;
    setPending(true);
    setFailure(null);
    try {
      await replace.mutateAsync({
        folderId: folder.id,
        version: acl.version,
        data: aclBody(draft),
      });
      notify({
        intent: 'success',
        message: t('permissions.done', { name: isolate(folder.name) }),
      });
      await refresh();
      onClose();
    } catch (error) {
      if (isStepUpRequired(error)) return;
      if (isStaleVersion(error)) {
        notify({ intent: 'warning', message: t('errors.stale') });
        await refresh();
        onClose();
      } else {
        setFailure(folderErrorMessage(t, error));
      }
    } finally {
      setPending(false);
    }
  };

  const inheritLabel = folder.parentId
    ? t('permissions.inheritParent')
    : t('permissions.inheritSpace');
  const inheritHint = folder.parentId
    ? t('permissions.inheritParentHint')
    : t('permissions.inheritSpaceHint');

  return (
    <>
      <DialogContent>
        <div className={styles.body}>
          <Switch
            label={inheritLabel}
            checked={!custom}
            onChange={(_event, data) => {
              change({ mode: data.checked ? 'inherit' : 'custom' });
            }}
          />
          <p className={styles.hint}>{custom ? t('permissions.customHint') : inheritHint}</p>

          {custom ? (
            <>
              {draft.entries.length > 0 ? (
                <ul className={styles.list} aria-label={t('permissions.listLabel')}>
                  {draft.entries.map((entry) => {
                    const name = isolate(nameOf(entry));
                    return (
                      <li key={`${entry.subjectType}:${entry.subjectId}`} className={styles.entry}>
                        <span className={styles.entryName}>
                          {entry.subjectType === 'organization_members' ? (
                            <People20Regular aria-hidden="true" />
                          ) : null}
                          <bdi>{nameOf(entry)}</bdi>
                        </span>
                        <Select
                          className={styles.relation}
                          aria-label={t('permissions.relationOf', { name })}
                          value={entry.relation}
                          onChange={(_event, data) => {
                            if (!isRelation(data.value)) return;
                            const relation = data.value;
                            change({
                              entries: draft.entries.map((other) =>
                                other === entry ? { ...other, relation } : other,
                              ),
                            });
                          }}
                        >
                          {RELATIONS.map((relation) => (
                            <option key={relation} value={relation}>
                              {t(`permissions.relations.${relation}`)}
                            </option>
                          ))}
                        </Select>
                        <Button
                          appearance="subtle"
                          icon={<Delete20Regular />}
                          aria-label={t('permissions.remove', { name })}
                          onClick={() => {
                            change({ entries: draft.entries.filter((other) => other !== entry) });
                          }}
                        />
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <MessageBar intent="warning" layout="multiline">
                  <MessageBarBody>{t('permissions.emptyCustom')}</MessageBarBody>
                </MessageBar>
              )}

              <div className={styles.adder}>
                {members.isSuccess && available.length > 0 ? (
                  <>
                    <FormField className={styles.adderField} label={t('permissions.addLabel')}>
                      <Select
                        value={pick}
                        onChange={(_event, data) => {
                          setPick(data.value);
                        }}
                      >
                        <option value="">{t('permissions.addPlaceholder')}</option>
                        {available.map((member) => (
                          <option key={member.userId} value={member.userId}>
                            {member.displayName}
                          </option>
                        ))}
                      </Select>
                    </FormField>
                    <Button
                      appearance="secondary"
                      icon={<PersonAdd20Regular />}
                      disabledFocusable={!pick}
                      onClick={addPerson}
                    >
                      {t('permissions.addButton')}
                    </Button>
                  </>
                ) : members.isSuccess ? (
                  <p className={styles.hint}>{t('permissions.noneToAdd')}</p>
                ) : members.isError ? (
                  <p className={styles.hint}>{t('permissions.membersFailed')}</p>
                ) : null}
                <Button
                  appearance="secondary"
                  icon={<People20Regular />}
                  disabledFocusable={everyoneListed}
                  onClick={addEveryone}
                >
                  {t('permissions.addAll')}
                </Button>
              </div>
            </>
          ) : null}

          {failure ? (
            <MessageBar intent="error" layout="multiline">
              <MessageBarBody>{failure}</MessageBarBody>
            </MessageBar>
          ) : null}
        </div>
      </DialogContent>
      <DialogActions>
        <Button appearance="secondary" onClick={onClose}>
          {t('common:actions.cancel')}
        </Button>
        <Button
          appearance="primary"
          disabledFocusable={!dirty || pending}
          onClick={() => {
            void save();
          }}
        >
          {pending ? t('permissions.saving') : t('permissions.save')}
        </Button>
      </DialogActions>
    </>
  );
}

function PermissionsLoader(props: Omit<FormProps, 'acl'>) {
  const { t } = useTranslation('documents');
  const acl = useFolderAcl(props.spaceId, props.folder.id);

  if (acl.isError && !acl.data) {
    return (
      <>
        <DialogContent>
          <MessageBar intent="error" layout="multiline">
            <MessageBarBody>{t('permissions.loadFailed')}</MessageBarBody>
            <MessageBarActions>
              <Button
                onClick={() => {
                  void acl.refetch();
                }}
              >
                {t('common:actions.retry')}
              </Button>
            </MessageBarActions>
          </MessageBar>
        </DialogContent>
        <DialogActions>
          <Button appearance="secondary" onClick={props.onClose}>
            {t('common:actions.close')}
          </Button>
        </DialogActions>
      </>
    );
  }
  if (!acl.data) {
    return (
      <DialogContent>
        <SkeletonList count={3} />
      </DialogContent>
    );
  }
  // The form starts again from the server's answer whenever the ACL changes.
  const signature = `${String(acl.data.version)}:${acl.data.mode}`;
  return <PermissionsForm key={signature} {...props} acl={acl.data} />;
}

export interface PermissionsDialogProps {
  orgId: string;
  spaceId: string;
  folder: Folder;
  onClose: () => void;
}

/**
 * Permissions of a folder (`can_manage`): inherit the parent's rights, or a custom list of people
 * and "all members of the office" with a reader / editor choice. Saved as a whole with `If-Match`.
 */
export function PermissionsDialog({ orgId, spaceId, folder, onClose }: PermissionsDialogProps) {
  const { t } = useTranslation('documents');
  return (
    <Dialog
      open
      modalType="modal"
      onOpenChange={(_event, data) => {
        if (!data.open) onClose();
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{t('permissions.title', { name: isolate(folder.name) })}</DialogTitle>
          <PermissionsLoader orgId={orgId} spaceId={spaceId} folder={folder} onClose={onClose} />
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}


import "/opt/batiflow/app/apps/web/src/features/documents/permissions-dialog.griffel.css";